/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { useFormState } from 'react-dom'
import { useWebApp } from '@/hooks/webapp'

// import server actions
import { uploadIconAction, uploadMediaAction } from '@/actions/webapps/image'

// import components
import { Input } from '@/components/input'
import { FlexButton } from '@/components/buttons'
import {
	Field,
	Fieldset,
	Label,
	Legend,
} from '@headlessui/react'

export const ImageSettings = () => {
	const webApp = useWebApp()

	const [[], uploadIcon] = useFormState(uploadIconAction, [null, null])
	const [[], uploadMedia] = useFormState(uploadMediaAction, [null, null])

	return (
		<Fieldset className='flex flex-col gap-3 p-3'>
			<div className='flex flex-col'>
				<Legend className='text-xl font-semibold'>Manage Images</Legend>
				<span className='text-xs font-light'>Upload the images used to identify this web app.</span>
			</div>
			<Field as='form' className='flex items-stretch gap-5' action={uploadIcon}>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>Icon</Label>
					<span className='text-xs font-light'>The icon for the web app.</span>
				</div>
				<div className='flex gap-3'>
					<img alt='Icon' src={`/api/images/webapps/${webApp.id}/icon`} className='flex aspect-video w-64 shrink-0 rounded border border-800 bg-700 object-contain text-transparent' />
					<div className='flex grow flex-col gap-3'>
						<input type='hidden' name='id' value={webApp.id} />
						<Input type='file' name='icon' accept='image/*' readOnly={!webApp.canEdit} required />
						<div>
							<FlexButton type='submit' disabled={!webApp.canEdit}>Upload Icon</FlexButton>
						</div>
					</div>
				</div>
			</Field>
			<Field as='form' className='flex items-stretch gap-5' action={uploadMedia}>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>Hero Image</Label>
					<span className='text-xs font-light'>A prominent image from the web app.</span>
				</div>
				<div className='flex gap-3'>
					<img alt='Hero' src={`/api/images/webapps/${webApp.id}/hero`} className='flex aspect-video w-64 shrink-0 rounded border border-800 bg-700 object-cover text-transparent' />
					<div className='flex grow flex-col gap-3'>
						<input type='hidden' name='id' value={webApp.id} />
						<input type='hidden' name='hero' value='true' />
						<Input type='file' name='media' accept='image/*, video/*' readOnly={!webApp.canEdit} required />
						<div>
							<FlexButton type='submit' disabled={!webApp.canEdit}>Upload Hero Image</FlexButton>
						</div>
					</div>
				</div>
			</Field>
		</Fieldset>
	)
}